import React, { useContext, useEffect, useState } from 'react';
import { BasicModalWithFooter, ErrorInpage, TextFieldMultiLine, TextRegular } from '@hz-design-system/web-ui';
import ReportReason from '../atoms/ReportReason';
import ReportingReasons from '../constants/ReportingReasons';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { FaqReportingContext, Step } from '../context/FaqReportingContext';
import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import flagPaaContent from '@/client/actions/flag/flagPaaContent';

const MAX_LENGTH = 500;

enum ReportingErrors {
  NO_REASON = 'NO_REASON',
  NO_MORE_INFO = 'NO_MORE_INFO',
  FAILED_REPORT = 'FAILED_REPORT',
  TEXT_TOO_LONG = 'TEXT_TOO_LONG',
}

const ReportingModal: React.FC = () => {
  const [errorReason, setErrorReason] = useState<ReportingErrors>();
  const { xsrfToken } = useContext(EnvironmentContext);

  const {
    modalOpen,
    selectedReason,
    additionalInfo,
    setAdditionalInfo,
    afterSubmit,
    currentStep,
    setCurrentStep,
    closeModal,
    searchRequestObject,
  } = useContext(FaqReportingContext);

  const { t } = useI18nContext();

  const handleUserMessage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAdditionalInfo(event.target.value);
  };

  useEffect(() => {
    if (currentStep === Step.THANK_YOU && !modalOpen) {
      setCurrentStep(Step.FORM);
    }
  }, [modalOpen, setCurrentStep, currentStep]);

  useEffect(() => {
    if (selectedReason === 'OTHER') {
      setErrorReason(additionalInfo && additionalInfo.length > MAX_LENGTH ? ReportingErrors.TEXT_TOO_LONG : undefined);
    } else {
      setErrorReason(undefined);
    }
  }, [selectedReason, additionalInfo, errorReason]);

  const submitReport = async () => {
    if (currentStep === Step.THANK_YOU) {
      closeModal();
      window.location.reload();
      return;
    }

    if (errorReason === ReportingErrors.TEXT_TOO_LONG) {
      return;
    }

    if (!selectedReason) {
      setErrorReason(ReportingErrors.NO_REASON);
      return;
    }

    if (selectedReason === 'OTHER' && !additionalInfo) {
      setErrorReason(ReportingErrors.NO_MORE_INFO);
      return;
    }

    // Extract values from search result data or use defaults
    const categories = searchRequestObject?.categories;
    const attributesValuesIds = searchRequestObject?.attributesById || [];
    const attributesByKey = searchRequestObject?.attributesByKey || [];
    const query = searchRequestObject?.searchQuery ?? undefined;

    const body = {
      query,
      flagReason: selectedReason,
      categories,
      attributesValuesIds,
      attributesByKey,
      moreInfo: additionalInfo ?? undefined,
    };

    try {
      await flagPaaContent({
        body,
        xsrfToken,
      });
      afterSubmit();
    } catch (error) {
      setErrorReason(ReportingErrors.FAILED_REPORT);
    }
  };

  return (
    <BasicModalWithFooter
      isOpen
      size="m"
      buttonsAlignmentDesktop="stacked"
      buttonsAlignmentMobile="stacked"
      title={t('seo.faqReporting.reportButton')}
      onClose={() => closeModal()}
      buttons={[
        {
          component: 'PrimaryButton',
          text: currentStep === Step.FORM ? t('seo.faqReporting.continueButton') : t('seo.faqReporting.finishButton'),
          onClick: () => submitReport(),
        },
      ]}
    >
      {currentStep === Step.FORM && (
        <>
          {errorReason && (
            <div>
              {errorReason === ReportingErrors.NO_REASON && (
                <ErrorInpage icon="alert" title={t('seo.faqReporting.noReasonError')} />
              )}
              {errorReason === ReportingErrors.NO_MORE_INFO && (
                <ErrorInpage icon="alert" title={t('seo.faqReporting.noMoreInfoError')} />
              )}
              {errorReason === ReportingErrors.FAILED_REPORT && (
                <ErrorInpage icon="alert" title={t('seo.faqReporting.failedReportError')} />
              )}
              {errorReason === ReportingErrors.TEXT_TOO_LONG && (
                <ErrorInpage icon="alert" title={t('seo.faqReporting.textTooLong')} />
              )}
            </div>
          )}

          <div className="informational-Text">
            <TextRegular>
              <Trans tagName="span" i18nKey="seo.faqReporting.informationalText" />
            </TextRegular>
          </div>
          {Object.entries(ReportingReasons).map(([reason, label]) => (
            <ReportReason key={reason} reason={reason} label={label.label} />
          ))}
          <div>
            <TextFieldMultiLine
              labelText={t('seo.faqReporting.moreInfoLabel')}
              maxLength={MAX_LENGTH}
              minRows={2}
              maxRows={10}
              charsRemainingText={t('seo.faqReporting.lengthTip')}
              onChange={handleUserMessage}
              isRequired={selectedReason === 'OTHER' ? true : false}
              value={additionalInfo}
            />
          </div>
        </>
      )}

      {currentStep === Step.THANK_YOU && (
        <div>
          <TextRegular>{t('seo.faqReporting.thankYou')}</TextRegular>
        </div>
      )}
    </BasicModalWithFooter>
  );
};

export default ReportingModal;
